import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Upload, Star, Camera, Receipt, Menu as MenuIcon } from 'lucide-react';

const pillars = [
  { kanji: '義', name: 'Giustizia', prompt: 'Valuta equità nei prezzi, rispetto per ingredienti e produttori, trasparenza nelle informazioni' },
  { kanji: '勇', name: 'Coraggio', prompt: 'Considera innovazione nel menu, audacia nelle combinazioni, determinazione nella qualità' },
  { kanji: '仁', name: 'Compassione', prompt: 'Osserva attenzione alle esigenze speciali, cura per gli ospiti, gentilezza del servizio' },
  { kanji: '礼', name: 'Rispetto', prompt: 'Valuta cortesia del personale, rispetto delle tradizioni, dignità dell\'ambiente' },
  { kanji: '誠', name: 'Onestà', prompt: 'Considera trasparenza su ingredienti, veridicità delle descrizioni, integrità nelle promesse' },
  { kanji: '忠義', name: 'Devozione', prompt: 'Osserva dedizione alla qualità, passione nel servizio, fedeltà ai propri principi' },
  { kanji: '清', name: 'Purezza', prompt: 'Valuta qualità degli ingredienti, semplicità delle preparazioni, autenticità dei sapori' }
];

const priceRanges = [
  { value: 'economico', label: '€ - Economico (0-25€)' },
  { value: 'medio', label: '€€ - Medio (25-50€)' },
  { value: 'alto', label: '€€€ - Alto (50-100€)' },
  { value: 'lusso', label: '€€€€ - Lusso (100€+)' }
];

export default function ReviewPage() {
  const [formData, setFormData] = useState({
    restaurantName: '',
    city: '',
    date: '',
    priceRange: '',
    pillarScores: Array(7).fill(3),
    pillarNotes: Array(7).fill(''),
    evidencePhotos: [],
    transparency: {
      sourcing: false,
      waste: false,
      accessibility: false,
      sourcingDesc: '',
      wasteDesc: '',
      accessibilityDesc: ''
    }
  });

  const [bushiScore, setBushiScore] = useState(0);
  const [microNarrative, setMicroNarrative] = useState('');

  const handlePillarScoreChange = (index: number, value: number[]) => {
    const newScores = [...formData.pillarScores];
    newScores[index] = value[0];
    setFormData({ ...formData, pillarScores: newScores });
    calculateBushiScore(newScores);
  };

  const handlePillarNoteChange = (index: number, note: string) => {
    const newNotes = [...formData.pillarNotes];
    newNotes[index] = note;
    setFormData({ ...formData, pillarNotes: newNotes });
  };

  const calculateBushiScore = (scores: number[]) => {
    // Pesi per ogni pilastro (possono essere personalizzati)
    const weights = [1.2, 1.0, 1.1, 1.0, 1.3, 1.0, 1.1]; // Giustizia e Onestà hanno peso maggiore
    const weightedSum = scores.reduce((sum, score, index) => sum + (score * weights[index]), 0);
    const maxWeightedSum = weights.reduce((sum, weight) => sum + (5 * weight), 0);
    const score = Math.round((weightedSum / maxWeightedSum) * 100);
    setBushiScore(score);
    generateMicroNarrative(scores);
  };

  const generateMicroNarrative = (scores: number[]) => {
    const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    
    let narrative = '';
    
    // Verità & Giustizia
    if (scores[0] >= 4 && scores[4] >= 4) {
      narrative += 'Un locale che onora la verità degli ingredienti con giustizia nei prezzi. ';
    } else {
      narrative += 'Spazio per migliorare nella trasparenza e nell\'equità. ';
    }
    
    // Rito & Rispetto
    if (scores[3] >= 4) {
      narrative += 'Il servizio rispetta i rituali dell\'ospitalità con eleganza. ';
    } else {
      narrative += 'Il rispetto per gli ospiti può essere rafforzato. ';
    }
    
    // Essenza & Coraggio
    if (scores[1] >= 4 && scores[6] >= 4) {
      narrative += 'Coraggio nell\'innovazione che preserva l\'essenza autentica.';
    } else {
      narrative += 'L\'equilibrio tra tradizione e innovazione cerca ancora la sua via.';
    }
    
    setMicroNarrative(narrative);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Qui implementeresti l'invio della recensione
    console.log('Recensione inviata:', formData, { bushiScore, microNarrative });
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-bushido text-4xl font-bold mb-4">
            Modulo <span className="gold-accent">Recensione Etica</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Valuta il locale secondo i Sette Pilastri del Bushidō. 
            Ogni recensione contribuisce a costruire un ecosistema culinario più etico.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informazioni Base */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MenuIcon className="mr-2 h-5 w-5 text-secondary" />
                Locale & Esperienza
              </CardTitle>
              <CardDescription>
                Informazioni di base sulla tua visita
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="restaurantName">Nome del Locale *</Label>
                <Input
                  id="restaurantName"
                  value={formData.restaurantName}
                  onChange={(e) => setFormData({ ...formData, restaurantName: e.target.value })}
                  placeholder="Es. Osteria del Samurai"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Città *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Es. Milano"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Data Visita *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priceRange">Fascia di Prezzo *</Label>
                <Select value={formData.priceRange} onValueChange={(value) => setFormData({ ...formData, priceRange: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona fascia di prezzo" />
                  </SelectTrigger>
                  <SelectContent>
                    {priceRanges.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Valutazione Pilastri */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Star className="mr-2 h-5 w-5 text-secondary" />
                Valutazione sui Sette Pilastri
              </CardTitle>
              <CardDescription>
                Valuta ogni pilastro da 1 a 5 e aggiungi una nota descrittiva (220-300 caratteri)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {pillars.map((pillar, index) => (
                <div key={index} className="space-y-4 p-4 border border-border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="kanji-display text-3xl">
                      {pillar.kanji}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bushido text-lg font-semibold">
                        {pillar.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {pillar.prompt}
                      </p>
                    </div>
                    <div className="text-center">
                      <Badge variant="secondary" className="mb-2">
                        {formData.pillarScores[index]}/5
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Punteggio (1-5)</Label>
                      <Slider
                        value={[formData.pillarScores[index]]}
                        onValueChange={(value) => handlePillarScoreChange(index, value)}
                        max={5}
                        min={1}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>1 - Insufficiente</span>
                        <span>3 - Sufficiente</span>
                        <span>5 - Eccellente</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Nota Descrittiva (220-300 caratteri)</Label>
                      <Textarea
                        value={formData.pillarNotes[index]}
                        onChange={(e) => handlePillarNoteChange(index, e.target.value)}
                        placeholder={`Descrivi come il locale esprime il pilastro ${pillar.name}...`}
                        className="min-h-[80px]"
                        maxLength={300}
                      />
                      <div className="text-xs text-muted-foreground text-right">
                        {formData.pillarNotes[index].length}/300 caratteri
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Evidenze */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Camera className="mr-2 h-5 w-5 text-secondary" />
                Evidenze Fotografiche
              </CardTitle>
              <CardDescription>
                Carica 1-5 foto (piatto, menu, scontrino) per supportare la tua recensione
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  Trascina le foto qui o clicca per selezionare
                </p>
                <Button variant="outline">
                  Seleziona File
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Formati supportati: JPG, PNG, WEBP (max 5MB per file)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Trasparenza */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Receipt className="mr-2 h-5 w-5 text-secondary" />
                Trasparenza Attiva
              </CardTitle>
              <CardDescription>
                Informazioni aggiuntive sulla sostenibilità e accessibilità del locale
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sourcing"
                    checked={formData.transparency.sourcing}
                    onCheckedChange={(checked) => 
                      setFormData({
                        ...formData,
                        transparency: { ...formData.transparency, sourcing: checked as boolean }
                      })
                    }
                  />
                  <Label htmlFor="sourcing" className="font-medium">
                    Sourcing Ingredienti
                  </Label>
                </div>
                {formData.transparency.sourcing && (
                  <Textarea
                    placeholder="Descrivi la provenienza e qualità degli ingredienti..."
                    value={formData.transparency.sourcingDesc}
                    onChange={(e) => 
                      setFormData({
                        ...formData,
                        transparency: { ...formData.transparency, sourcingDesc: e.target.value }
                      })
                    }
                  />
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="waste"
                    checked={formData.transparency.waste}
                    onCheckedChange={(checked) => 
                      setFormData({
                        ...formData,
                        transparency: { ...formData.transparency, waste: checked as boolean }
                      })
                    }
                  />
                  <Label htmlFor="waste" className="font-medium">
                    Gestione Rifiuti
                  </Label>
                </div>
                {formData.transparency.waste && (
                  <Textarea
                    placeholder="Descrivi le pratiche di sostenibilità e riduzione sprechi..."
                    value={formData.transparency.wasteDesc}
                    onChange={(e) => 
                      setFormData({
                        ...formData,
                        transparency: { ...formData.transparency, wasteDesc: e.target.value }
                      })
                    }
                  />
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="accessibility"
                    checked={formData.transparency.accessibility}
                    onCheckedChange={(checked) => 
                      setFormData({
                        ...formData,
                        transparency: { ...formData.transparency, accessibility: checked as boolean }
                      })
                    }
                  />
                  <Label htmlFor="accessibility" className="font-medium">
                    Accessibilità
                  </Label>
                </div>
                {formData.transparency.accessibility && (
                  <Textarea
                    placeholder="Descrivi l'accessibilità del locale e l'attenzione alle esigenze speciali..."
                    value={formData.transparency.accessibilityDesc}
                    onChange={(e) => 
                      setFormData({
                        ...formData,
                        transparency: { ...formData.transparency, accessibilityDesc: e.target.value }
                      })
                    }
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Output Preview */}
          {bushiScore > 0 && (
            <Card className="bg-gradient-to-r from-muted/50 to-muted/30 border-secondary/30">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Anteprima Risultato</span>
                  <Badge variant="secondary" className="text-lg px-4 py-2">
                    BushiScore: {bushiScore}/100
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Micro-narrazione:</h4>
                  <p className="text-sm italic bg-muted/50 p-3 rounded border-l-4 border-secondary">
                    {microNarrative}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {bushiScore >= 80 && <Badge className="bg-green-600">Eccellente</Badge>}
                  {bushiScore >= 60 && bushiScore < 80 && <Badge className="bg-yellow-600">Buono</Badge>}
                  {bushiScore >= 40 && bushiScore < 60 && <Badge className="bg-orange-600">Sufficiente</Badge>}
                  {bushiScore < 40 && <Badge className="bg-red-600">Da Migliorare</Badge>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          <div className="text-center">
            <Button type="submit" size="lg" className="btn-samurai px-12 py-3">
              Pubblica Recensione
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}